import { useEffect, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { video } from '../services/api';

function describePlayerError(error) {
  const code = error?.data?.nErrorCode ?? error?.data?.errorCode ?? error?.code ?? error?.type;
  const message = error?.response?.data?.message || error?.msg || error?.message || '视频播放失败';
  return code !== undefined && code !== null ? `${message}（错误码：${code}）` : message;
}

export default function EzvizPlayer({ camera, compact = false, onPlayer }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('正在获取播放凭证');
  const playerId = `ezviz-player-${camera.id}`;

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    const createPlayer = async () => {
      setLoading(true);
      setError(null);
      setStage('正在获取播放凭证');
      try {
        const credentialRes = await video.playCredentials(camera.id);
        if (cancelled) return;
        setStage('凭证已获取，正在加载播放器');
        const { EZUIKitPlayer } = await import('ezuikit-js');
        if (cancelled) return;
        const width = Math.max(320, Math.min(containerRef.current?.clientWidth || 640, 960));
        const player = new EZUIKitPlayer({
          id: playerId,
          width,
          height: compact ? 220 : Math.round(width * 9 / 16),
          accessToken: credentialRes.data.accessToken,
          url: credentialRes.data.url,
          hd: camera.stream_type === 'main',
          template: 'pcLive',
          streamInfoCBType: 1,
          handleError: (playerError) => {
            if (cancelled) return;
            setError(describePlayerError(playerError));
            setLoading(false);
          },
        });
        playerRef.current = player;
        onPlayer?.(player);
        player.eventEmitter.on(EZUIKitPlayer.EVENTS.decoderLoad, () => {
          if (!cancelled) setStage('正在加载解码库');
        });
        player.eventEmitter.on(EZUIKitPlayer.EVENTS.decoderLoaded, () => {
          if (!cancelled) setStage('解码库已加载，正在取流');
        });
        player.eventEmitter.on(EZUIKitPlayer.EVENTS.streamInfoCB, () => {
          if (!cancelled) setStage('已获取视频流，等待首帧');
        });
        player.eventEmitter.on(EZUIKitPlayer.EVENTS.firstFrameDisplay, () => {
          if (!cancelled) {
            setStage('播放正常');
            setLoading(false);
            clearTimeout(timeoutId);
          }
        });
        await player.play();
        if (!cancelled) setStage('播放指令已完成，等待首帧');
        timeoutId = setTimeout(() => {
          if (!cancelled) {
            setError('30 秒内未收到视频首帧，请检查设备在线状态、码流配置及浏览器网络');
            setLoading(false);
          }
        }, 30000);
      } catch (err) {
        if (!cancelled) {
          setError(describePlayerError(err));
          setLoading(false);
        }
      }
    };

    createPlayer();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (playerRef.current) {
        try {
          playerRef.current.stop?.();
          playerRef.current.destroy?.();
        } catch (err) {
          // 播放器已销毁时无需重复处理
        }
        playerRef.current = null;
        onPlayer?.(null);
      }
    };
  }, [camera.id, camera.stream_type, playerId]);

  return (
    <div ref={containerRef} style={{ position: 'relative', minHeight: compact ? 220 : 320, background: '#0b1220', borderRadius: 8, overflow: 'hidden' }}>
      {loading && <Spin style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }} />}
      {error && (
        <Alert
          type="warning" showIcon message="暂时无法播放"
          description={error}
          style={{ position: 'absolute', left: 16, right: 16, top: 16, zIndex: 3 }}
        />
      )}
      <div style={{ position: 'absolute', left: 8, bottom: 8, zIndex: 4, padding: '2px 6px', color: '#d6ecff', background: 'rgba(0,0,0,0.65)', fontSize: 12 }}>{stage}</div>
      <div id={playerId} style={{ width: '100%', minHeight: compact ? 220 : 320 }} />
    </div>
  );
}
